/**
 * Bidirectional Sticky Columns for Product Page
 *
 * A three-phase approach using position:sticky throughout:
 *
 *   PHASE 1 – STICK TOP:   Element pinned below header (top = topOffset)
 *   PHASE 2 – SCROLLING:   Element scrolls with page (top = current visual position)
 *   PHASE 3 – STICK BOTTOM: Element bottom pinned at viewport bottom (top = vpH - elH)
 *
 * On direction change, we calculate a `top` value that exactly matches
 * the element's current visual position to prevent jumps. As the user
 * keeps scrolling, the element naturally reaches the next stick-point.
 *
 * Only active on desktop (≥750px). Falls back to CSS sticky on no-JS.
 */
(function () {
  'use strict';

  var MOBILE_BP = 750;
  var TOP_GAP = 16;
  var BOTTOM_GAP = 16;

  function getTopOffset() {
    var header = document.querySelector('sticky-header');
    var h = header ? header.offsetHeight : 0;
    if (!h) {
      var v = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      h = v ? parseFloat(v) : 0;
    }
    return h + TOP_GAP;
  }

  function init() {
    if (window.innerWidth < MOBILE_BP) return;

    var section = document.querySelector('section[id^="MainProduct"]');
    if (!section) return;

    var els = section.querySelectorAll('.product__column-sticky');
    if (!els.length) return;

    var topOffset = getTopOffset();
    var lastY = window.scrollY;
    var lastDir = 0;

    // Per-element state
    var colState = [];
    els.forEach(function (el, i) {
      colState[i] = { currentTop: topOffset };
      el.style.top = topOffset + 'px';
    });

    var ticking = false;

    function onScroll() {
      // Below the mobile breakpoint the sticky behaviour is disabled, so skip the
      // layout reads/writes instead of leaving them running after a desktop->mobile resize.
      if (window.innerWidth < MOBILE_BP) return;
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.scrollY;
        var delta = y - lastY;
        if (delta === 0) { ticking = false; return; }

        var dir = delta > 0 ? 1 : -1;
        var dirChanged = dir !== lastDir && lastDir !== 0;
        var vpH = window.innerHeight;

        els.forEach(function (el, i) {
          var s = colState[i];
          var elH = el.offsetHeight;

          // If element fits in viewport with gaps, simple top-stick
          if (elH <= vpH - topOffset - BOTTOM_GAP) {
            s.currentTop = topOffset;
            el.style.top = topOffset + 'px';
            return;
          }

          var maxTop = topOffset;                     // stick-at-top value
          var minTop = vpH - elH - BOTTOM_GAP;        // stick-at-bottom value (negative for tall elements)

          if (dirChanged) {
            // Direction just changed. To avoid a visual jump, we need to
            // set `top` to a value that keeps the element exactly where it
            // visually is right now.
            //
            // With position:sticky, the displayed top = max(top, naturalTop)
            // where naturalTop = el.offsetTop - scrollY.
            //
            // The element's current visual position (rect.top) tells us
            // where it is. We want to set `top` = rect.top so that:
            //   - If scrolling UP: as user scrolls, naturalTop increases,
            //     eventually exceeding our freezeTop and element scrolls free,
            //     then hits maxTop and sticks.
            //   - If scrolling DOWN: as user scrolls, naturalTop decreases,
            //     element stays at freezeTop, which IS the sticky behavior,
            //     then when naturalTop < minTop and we've set top to minTop,
            //     element sticks at bottom.
            //
            // But we must clamp between minTop and maxTop.
            var rect = el.getBoundingClientRect();
            var freezeTop = Math.max(minTop, Math.min(maxTop, rect.top));
            s.currentTop = freezeTop;
            el.style.top = freezeTop + 'px';
          } else if (lastDir === 0) {
            // First scroll — set direction-based sticky
            if (dir === 1) {
              s.currentTop = minTop;
              el.style.top = minTop + 'px';
            }
          }

          // If not a direction change, check if we should transition to a stick-point
          if (!dirChanged && dir === 1 && s.currentTop > minTop) {
            // Scrolling down but not yet at bottom stick — check if bottom reached viewport
            var rect2 = el.getBoundingClientRect();
            if (rect2.bottom <= vpH - BOTTOM_GAP) {
              s.currentTop = minTop;
              el.style.top = minTop + 'px';
            }
          } else if (!dirChanged && dir === -1 && s.currentTop < maxTop) {
            // Scrolling up but not yet at top stick — check if top reached header
            var rect3 = el.getBoundingClientRect();
            if (rect3.top >= maxTop) {
              s.currentTop = maxTop;
              el.style.top = maxTop + 'px';
            }
          }
        });

        lastDir = dir;
        lastY = y;
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    // Handle dynamic content changes
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        if (window.innerWidth < MOBILE_BP) return;
        var vpH = window.innerHeight;
        els.forEach(function (el, i) {
          var s = colState[i];
          var elH = el.offsetHeight;
          if (elH <= vpH - topOffset - BOTTOM_GAP) {
            s.currentTop = topOffset;
            el.style.top = topOffset + 'px';
          } else {
            // Recalculate minTop and clamp
            var minTop = vpH - elH - BOTTOM_GAP;
            if (s.currentTop < minTop) {
              s.currentTop = minTop;
              el.style.top = minTop + 'px';
            }
          }
        });
      });
      els.forEach(function (el) { ro.observe(el); });
    }

    // Handle breakpoint crossing
    var mql = window.matchMedia('(min-width: ' + MOBILE_BP + 'px)');
    mql.addEventListener('change', function (e) {
      if (!e.matches) {
        els.forEach(function (el) { el.style.top = ''; });
      } else {
        topOffset = getTopOffset();
        lastY = window.scrollY;
        lastDir = 0;
        els.forEach(function (el, i) {
          colState[i].currentTop = topOffset;
          el.style.top = topOffset + 'px';
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
